import React from 'react';
import {
  Box,
  Typography,
  Stack,
  IconButton,
  Paper,
  Button,
  Divider
} from '@mui/material';
import {
  LocationOn,
  Close
} from '@mui/icons-material';
import { OperationLocation } from '../../../../networkRequest/operationsService';

interface LocationAddressProps {
  location?: OperationLocation;
  locations?: OperationLocation[];
  primary?: boolean;
  sectionTitle?: string;
  actionButtonText?: string;
  actionButtonIcon?: React.ReactNode;
  onAction?: () => void;
  onRemove?: (locationId: string | undefined) => void;
}

const LocationAddress: React.FC<LocationAddressProps> = ({
  location,
  locations,
  primary = false,
  sectionTitle,
  actionButtonText,
  actionButtonIcon,
  onAction,
  onRemove
}) => {
  const formatAddress = (location: OperationLocation) => {
    // First line: street address
    const streetParts = [
      location.street_address_1,
      location.street_address_2
    ].filter(Boolean);
    const streetAddress = streetParts.join(' ');
    
    // Second line: city, state zip
    const cityStateParts = [
      location.city,
      location.state,
      location.zip
    ].filter(Boolean);
    const cityStateZip = cityStateParts.join(', ');
    
    return { streetAddress, cityStateZip };
  };

  const renderLocationItem = (loc: OperationLocation, showRemove = false) => {
    const { streetAddress, cityStateZip } = formatAddress(loc);
    
    return (
      <Stack
        direction="row"
        spacing={2}
        sx={{
          alignItems: "flex-start",
          py: 1
        }}>
        <LocationOn sx={{ color: 'text.secondary' }} />
        <Box sx={{ width: '497px' }}>
          <Typography variant={primary ? "h6" : "body1"}>{loc.name}</Typography>
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography variant="body1" sx={{
            color: "text.primary"
          }}>
            {streetAddress}
          </Typography>
          <Typography variant="body1" sx={{
            color: "text.primary"
          }}>
            {cityStateZip}
          </Typography>
        </Box>
        {showRemove && onRemove && (
          <IconButton size="small" onClick={() => onRemove(loc.id)}>
            <Close />
          </IconButton>
        )}
      </Stack>
    );
  };

  return (
    <Paper
      elevation={0}
      sx={{
        backgroundColor: 'blueGrey.50',
        p: 2,
        borderRadius: '10px',
      }}
    >
      {sectionTitle && (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            {sectionTitle}
          </Typography>
          {actionButtonText && onAction && (
            <Button
              size="small"
              startIcon={actionButtonIcon}
              onClick={onAction}
              variant="outlined"
              sx={actionButtonText === "Edit" || actionButtonText === "Location" ? {
                border: '1px solid #536d8680',
                color: '#536d86',
                '& .MuiSvgIcon-root': {
                  color: '#536d86',
                },
              } : {}}
            >
              {actionButtonText}
            </Button>
          )}
        </Box>
      )}
      
      {/* Render single location */}
      {location && renderLocationItem(location, !primary)}
      
      {/* Render multiple locations with dividers */}
      {locations && (
        <Stack spacing={0} divider={<Divider />}>
          {locations.map((loc) => (
            <div key={loc.id}>
              {renderLocationItem(loc, true)}
            </div>
          ))}
        </Stack>
      )}
    </Paper>
  );
};

export default LocationAddress;